import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  LayoutDashboard,
  Send,
  Loader2,
} from 'lucide-react';

import { dashboardApi } from '@/services/dashboard-api';
import { handleApiError } from '@/services/api';

const NewDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      const result = await dashboardApi.create({
        name: name.trim(),
      });
      return result.data;
    },
    onSuccess: (dashboard) => {
      const encodedPrompt = encodeURIComponent(prompt.trim());
      navigate(`/dashboards/${dashboard.id}/build?prompt=${encodedPrompt}`);
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to create dashboard');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !prompt.trim()) return;
    createMutation.mutate();
  };

  const isValid = name.trim() && prompt.trim();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/dashboards"
            className="p-2 rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-muted-foreground" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/70 to-primary flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                New Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Build a custom dashboard with AI
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dashboard Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Dashboard Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Monthly Revenue Overview"
              className={clsx(
                'w-full rounded-xl border border-input',
                'bg-card px-4 py-3',
                'text-sm text-foreground',
                'placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent'
              )}
            />
          </div>

          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Describe your ideal dashboard
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. I want a dashboard showing my restaurant's monthly revenue breakdown, expense categories, and year-over-year comparisons..."
              rows={4}
              className={clsx(
                'w-full rounded-xl border border-input',
                'bg-card px-4 py-3',
                'text-sm text-foreground',
                'placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
                'resize-none'
              )}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2">
            <Link
              to="/dashboards"
              className="px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={!isValid || createMutation.isPending}
              className={clsx(
                'flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg',
                'bg-primary text-white',
                'hover:bg-primary transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Start Building
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewDashboardPage;
